import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AsaasBillingPeriod, centsToReais } from '@/constants/asaas-pricing';
import { getPlanPrice, getPlanCommercialName } from '@/constants/plans';
import { usePaymentMethodCollection } from './usePaymentMethodCollection';
import { debugLogger } from '@/utils/debugLogger';

// Helper para determinar o role baseado no planId
const getRoleFromPlanId = (planId: string): 'sponsor' | 'sponsored' => {
  if (planId.startsWith('sponsored_')) return 'sponsored';
  return 'sponsor';
};

export const useAsaasCheckout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paymentMethodCollection = usePaymentMethodCollection();

  const createCheckoutSession = async (planId: string, billingPeriod: AsaasBillingPeriod) => {
    try {
      setIsLoading(true);
      setError(null);

      debugLogger.info('asaas', 'Creating checkout session:', { planId, billingPeriod });

      // For paid plans, collect payment method first
      const isPaidPlan = planId !== 'sponsor_free' && planId !== 'sponsored_free';
      let paymentData: { paymentMethod: 'PIX' | 'CREDIT_CARD' | null; pixData?: any } = { paymentMethod: null };

      if (isPaidPlan) {
        try {
          // Get plan name and value dynamically from constants
          const planName = getPlanCommercialName(planId);
          const role = getRoleFromPlanId(planId);
          const priceInCents = getPlanPrice(planId as any, billingPeriod, role);
          
          if (!priceInCents && planId !== 'sponsor_free' && planId !== 'sponsored_free') {
            throw new Error(`Preço não encontrado para plano ${planId} com período ${billingPeriod}`);
          }
          
          const planValue = centsToReais(priceInCents);
          
          // Collect payment method via modal
          paymentData = await paymentMethodCollection.openModal(planName, planValue, planId, billingPeriod);
          debugLogger.info('asaas', 'Payment data collected successfully');
        } catch (paymentError) {
          debugLogger.info('asaas', 'Payment data collection cancelled or failed:', paymentError);
          return; // User cancelled payment data collection
        }
      }

      // Handle PIX payments differently - no need to call create-asaas-checkout
      if (paymentData.paymentMethod === 'PIX' && paymentData.pixData) {
        debugLogger.info('asaas', 'PIX payment selected - QR code already generated');
        return { 
          success: true, 
          paymentMethod: 'PIX',
          pixData: paymentData.pixData 
        };
      }

      // For credit card payments (when available)
      const { data, error } = await supabase.functions.invoke('create-asaas-checkout', {
        body: {
          planId,
          billingPeriod,
          paymentMethod: paymentData.paymentMethod
        }
      });

      if (error) {
        debugLogger.error('asaas', 'Checkout error:', error);
        throw error;
      }

      // Log Asaas environment info
      if (data?.asaasEnvironment) {
        debugLogger.debug('asaas', 'Environment info:', {
          environment: data.asaasEnvironment.environment,
          baseUrl: data.asaasEnvironment.baseUrl,
          function: 'create-asaas-checkout'
        });
      }

      if (!data?.url) {
        throw new Error('No checkout URL received');
      }

      debugLogger.info('asaas', 'Checkout session created successfully:', data);
      
      // Redirect to Asaas Checkout in a new tab
      window.open(data.url, '_blank');
      
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao criar sessão de pagamento';
      debugLogger.error('asaas', 'Checkout error:', err);
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createCheckoutSession,
    isLoading,
    error,
    clearError: () => setError(null),
    paymentModal: {
      isOpen: paymentMethodCollection.isModalOpen,
      planName: paymentMethodCollection.planName,
      planValue: paymentMethodCollection.planValue,
      isLoading: paymentMethodCollection.isLoading,
      pixData: paymentMethodCollection.pixData,
      onClose: paymentMethodCollection.closeModal,
      onPixGenerate: paymentMethodCollection.generatePixQrCode,
      onPaymentMethodSelect: paymentMethodCollection.handlePaymentMethodSelect
    }
  };
};